import React, { useState } from "react";
import styled from "styled-components";
import { COLORS } from "../../../helpers/designConstants";

const TableContainer = styled.div`
  overflow-x: auto;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    border-radius: 6px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  min-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    min-width: 550px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    min-width: 500px;
  }
`;

const TableHeader = styled.thead`
  background-color: #f8fafc;
  border-bottom: 1px solid ${COLORS.border};
`;

const TableHeaderCell = styled.th`
  text-align: left;
  padding: 12px 16px;
  color: ${COLORS.textSecondary};
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: 10px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${COLORS.border};
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8fafc;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  color: ${COLORS.textPrimary};
  vertical-align: middle;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  
  background-color: ${props => {
    switch (props.status) {
      case 'success': return '#ecfdf5';
      case 'failed': return '#fef2f2';
      case 'running': return '#fef3c7';
      case 'partial': return '#eff6ff';
      default: return '#f3f4f6';
    }
  }};
  
  color: ${props => {
    switch (props.status) {
      case 'success': return '#059669';
      case 'failed': return '#dc2626';
      case 'running': return '#d97706';
      case 'partial': return '#2563eb';
      default: return '#6b7280';
    }
  }};
`;

const StatusDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: currentColor;
`;

const TriggerBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  background-color: ${props => props.trigger === 'manual' ? '#eff6ff' : '#f3f4f6'};
  color: ${props => props.trigger === 'manual' ? '#2563eb' : '#6b7280'};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
`;

const ExpandedRow = styled.tr`
  background-color: #f8fafc;
`;

const ExpandedCell = styled.td`
  padding: 16px;
  border-bottom: 1px solid ${COLORS.border};
`;

const ExpandedContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  max-width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const MetricGroup = styled.div`
  background: white;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid ${COLORS.border};
  
  @media (max-width: 480px) {
    padding: 8px;
    border-radius: 4px;
  }
`;

const MetricTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MetricLabel = styled.span`
  font-size: 13px;
  color: ${COLORS.textSecondary};
`;

const MetricValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.textPrimary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${COLORS.textSecondary};
`;


const formatDuration = (ms) => {
  if (!ms) return 'N/A';
  
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return 'N/A';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const ExecutionHistoryTable = ({ executions, isLoading }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (executionId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(executionId)) {
      newExpanded.delete(executionId);
    } else {
      newExpanded.add(executionId);
    }
    setExpandedRows(newExpanded);
  };

  if (isLoading && (!executions || executions.length === 0)) {
    return (
      <TableContainer>
        <EmptyState>Loading execution history...</EmptyState>
      </TableContainer>
    );
  }

  if (!executions || executions.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <p>No execution history available</p>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <tr>
            <TableHeaderCell>Execution ID</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Trigger</TableHeaderCell>
            <TableHeaderCell>Duration</TableHeaderCell>
            <TableHeaderCell>Products</TableHeaderCell>
            <TableHeaderCell>Started</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {executions.map((execution) => {
            const isExpanded = expandedRows.has(execution.id);
            // Use a simple time format instead of useTimeAgo hook to avoid hooks violation
            const timeDisplay = execution.startTime ? 
              new Date(execution.startTime).toLocaleString() : 'N/A';
            
            return (
              <React.Fragment key={execution.id}>
                <TableRow onClick={() => toggleRowExpansion(execution.id)}>
                  <TableCell>
                    <code style={{ fontSize: '12px', color: COLORS.secondary }}>
                      {execution.id.split('_').slice(-1)[0]}
                    </code>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={execution.status}>
                      <StatusDot />
                      {execution.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <TriggerBadge trigger={execution.trigger}>
                      {execution.trigger}
                    </TriggerBadge>
                  </TableCell>
                  <TableCell>
                    {formatDuration(execution.duration)}
                  </TableCell>
                  <TableCell>
                    {execution.stats?.productsProcessed?.toLocaleString() || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {timeDisplay}
                  </TableCell>
                </TableRow>
                
                {isExpanded && (
                  <ExpandedRow>
                    <ExpandedCell colSpan={6}>
                      <ExpandedContent>
                        <MetricGroup>
                          <MetricTitle>Statistics</MetricTitle>
                          <MetricRow>
                            <MetricLabel>Products Processed</MetricLabel>
                            <MetricValue>
                              {execution.stats?.productsProcessed?.toLocaleString() || 'N/A'}
                            </MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>API Calls</MetricLabel>
                            <MetricValue>{execution.stats?.apiCalls || 'N/A'}</MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Typesense Updates</MetricLabel>
                            <MetricValue>
                              {execution.stats?.typesenseUpdates?.toLocaleString() || 'N/A'}
                            </MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Errors</MetricLabel>
                            <MetricValue>{execution.stats?.errors || 0}</MetricValue>
                          </MetricRow>
                        </MetricGroup>

                        <MetricGroup>
                          <MetricTitle>Performance</MetricTitle>
                          <MetricRow>
                            <MetricLabel>API Call Duration</MetricLabel>
                            <MetricValue>
                              {formatDuration(execution.performance?.apiCallDuration)}
                            </MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Parsing Duration</MetricLabel>
                            <MetricValue>
                              {formatDuration(execution.performance?.parsingDuration)}
                            </MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Typesense Duration</MetricLabel>
                            <MetricValue>
                              {formatDuration(execution.performance?.typesenseUpdateDuration)}
                            </MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Total Duration</MetricLabel>
                            <MetricValue>
                              {formatDuration(execution.performance?.totalDuration || execution.duration)}
                            </MetricValue>
                          </MetricRow>
                        </MetricGroup>

                        {/* API Response Sizes MetricGroup */}
                        {execution.stats?.apiResponseSizes && (
                          <MetricGroup>
                            <MetricTitle>API Response Sizes</MetricTitle>
                            <MetricRow>
                              <MetricLabel>Total Downloaded</MetricLabel>
                              <MetricValue>
                                {formatBytes(execution.stats.apiResponseSizes.totalBytes)}
                              </MetricValue>
                            </MetricRow>
                            {execution.stats.apiResponseSizes.activeRegions && (
                              <MetricRow>
                                <MetricLabel>ActiveRegions</MetricLabel>
                                <MetricValue>
                                  {formatBytes(execution.stats.apiResponseSizes.activeRegions)}
                                </MetricValue>
                              </MetricRow>
                            )}
                            {execution.stats.apiResponseSizes.inventory && (
                              <MetricRow>
                                <MetricLabel>Inventory</MetricLabel>
                                <MetricValue>
                                  {formatBytes(execution.stats.apiResponseSizes.inventory)}
                                </MetricValue>
                              </MetricRow>
                            )}
                          </MetricGroup>
                        )}

                        <MetricGroup>
                          <MetricTitle>Execution Details</MetricTitle>
                          <MetricRow>
                            <MetricLabel>Environment</MetricLabel>
                            <MetricValue style={{ textTransform: 'capitalize' }}>
                              {execution.environment || 'N/A'}
                            </MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Started At</MetricLabel>
                            <MetricValue>
                              {execution.startTime?.toLocaleString() || 'N/A'}
                            </MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Completed At</MetricLabel>
                            <MetricValue>
                              {execution.endTime?.toLocaleString() || 'Running...'}
                            </MetricValue>
                          </MetricRow>
                          {execution.triggeredBy && (
                            <MetricRow>
                              <MetricLabel>Triggered By</MetricLabel>
                              <MetricValue>
                                {execution.triggeredBy}
                              </MetricValue>
                            </MetricRow>
                          )}
                          {execution.error && (
                            <MetricRow>
                              <MetricLabel>Error</MetricLabel>
                              <MetricValue style={{ color: COLORS.error }}>
                                {execution.error}
                              </MetricValue>
                            </MetricRow>
                          )}
                        </MetricGroup>
                      </ExpandedContent>
                    </ExpandedCell>
                  </ExpandedRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExecutionHistoryTable;